import type { Part } from '~/types/Design'
import type { Color } from '~/types/Color'

/**
 * パーツと色の素材互換性をチェックする
 * @param part - チェック対象のパーツ
 * @param color - チェック対象の色
 * @returns 素材IDが一致する場合はtrue、それ以外はfalse
 */
export function isColorValidForPart(part: Part, color: Color): boolean {
  return part.material.id === color.material.id
}

/**
 * 特定のパーツに対して有効な色のリストを取得する
 * @param part - 対象のパーツ
 * @param colors - 利用可能な色のリスト
 * @returns パーツの素材と一致する色のみの配列
 */
export function getValidColorsForPart(
  part: Part,
  colors: readonly Color[]
): Color[] {
  return colors.filter((color) => isColorValidForPart(part, color))
}

/**
 * 素材ごとに色をグループ化する
 * @param colors - グループ化する色のリスト
 * @returns 素材IDをキー、その素材を持つ色の配列を値とするMap
 */
export function groupColorsByMaterial(
  colors: readonly Color[]
): Map<string, Color[]> {
  const materialMap = new Map<string, Color[]>()

  for (const color of colors) {
    const materialId = color.material.id
    const existingColors = materialMap.get(materialId) || []
    materialMap.set(materialId, [...existingColors, color])
  }

  return materialMap
}

/**
 * パーツごとに有効な色のマップを作成する
 * @param parts - 対象のパーツリスト
 * @param colors - 利用可能な色のリスト
 * @returns パーツIDをキー、有効な色の配列を値とするMap
 */
export function createPartColorMap(
  parts: readonly Part[],
  colors: readonly Color[]
): Map<string, Color[]> {
  const partColorMap = new Map<string, Color[]>()

  for (const part of parts) {
    const validColors = getValidColorsForPart(part, colors)
    partColorMap.set(part.id, validColors)
  }

  return partColorMap
}
